import {Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {NgxChartsModule, Color, ScaleType} from '@swimlane/ngx-charts';
import {Observable} from 'rxjs';
import {TrendGranularity, TrendPoint, MasterTopUserUpdateStats} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

@Component({
  selector: 'app-version-and-updates',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './version-and-updates.component.html',
  styleUrls: ['./version-and-updates.component.css']
})
export class VersionAndUpdatesComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mode$: Observable<PeriodMode> = toObservable(this.periodService.mode);

  versionGranularity = signal<TrendGranularity>('DAY');
  updatesGranularity = signal<TrendGranularity>('DAY');
  isLoading = signal<boolean>(true);

  versionTrend: TrendPoint[] = [];
  aggregatedUpdaters: { userId: number; firstName: string; lastName: string; total: number }[] = [];

  areaChartData: any[] = [];
  areaColorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#E8A838']
  };

  private gradientColors: string[][] = [
    ['#7770CD', '#5A54B0'],
    ['#E8A838', '#C48A20'],
    ['#5AA155', '#3D7A3A'],
    ['#D33A3A', '#B02828'],
    ['#3A8DD3', '#2670B0'],
    ['#CD70B8', '#B054A0'],
    ['#70CDC4', '#54B0A8'],
    ['#CDA770', '#B08A54']
  ];

  ngOnInit(): void {
    this.mode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadAll();
    });
  }

  onVersionGranularityChange(g: TrendGranularity): void {
    this.versionGranularity.set(g);
    this.loadVersionTrend();
  }

  onUpdatesGranularityChange(g: TrendGranularity): void {
    this.updatesGranularity.set(g);
    this.loadMasterUpdates();
  }

  private loadAll(): void {
    this.isLoading.set(true);
    let pending = 2;
    const done = () => { if (--pending === 0) this.isLoading.set(false); };
    this.loadVersionTrend(done);
    this.loadMasterUpdates(done);
  }

  private loadVersionTrend(done?: () => void): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    this.statsService.getVersionTrend(start, end, this.versionGranularity())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.versionTrend = data;
          this.areaChartData = this.transformVersionData();
          done?.();
        },
        error: () => done?.()
      });
  }

  private loadMasterUpdates(done?: () => void): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    this.statsService.getTopMasterUpdates(start, end, this.updatesGranularity())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.aggregatedUpdaters = this.aggregateUpdaters(data);
          done?.();
        },
        error: () => done?.()
      });
  }

  private transformVersionData(): any[] {
    if (!this.versionTrend || !this.versionTrend.length) return [];

    return [{
      name: 'Versions',
      series: this.versionTrend.map(t => ({name: t.period, value: t.count}))
    }];
  }

  private aggregateUpdaters(raw: MasterTopUserUpdateStats[]): { userId: number; firstName: string; lastName: string; total: number }[] {
    const latestPeriod = this.getLatestPeriod(raw.map(r => r.period));
    const filtered = latestPeriod
      ? raw.filter(r => r.period === latestPeriod)
      : raw;

    const map = new Map<number, { userId: number; firstName: string; lastName: string; total: number }>();
    for (const entry of filtered) {
      const existing = map.get(entry.userId);
      if (existing) {
        existing.total += entry.totalUpdates;
      } else {
        map.set(entry.userId, {
          userId: entry.userId,
          firstName: entry.firstName,
          lastName: entry.lastName,
          total: entry.totalUpdates
        });
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }

  private getLatestPeriod(periods: string[]): string | null {
    if (!periods.length) return null;
    return [...new Set(periods)].sort().pop() || null;
  }

  get maxUpdaterTotal(): number {
    if (this.aggregatedUpdaters.length === 0) return 1;
    return Math.max(...this.aggregatedUpdaters.map(u => u.total), 1);
  }

  getBarWidth(total: number): string {
    return ((total / this.maxUpdaterTotal) * 100) + '%';
  }

  readonly barColors: string[] = ['#7770CD', '#5AA155', '#E8A838', '#D33A3A', '#3A8DD3'];

  getBarColor(index: number): string {
    return this.barColors[index % this.barColors.length];
  }

  formatXAxisTick = (val: string): string => {
    if (!val) return val;
    const parts = val.split('-');
    if (parts.length >= 3) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = parseInt(parts[1], 10) - 1;
      return `${months[monthIdx]} ${parseInt(parts[2], 10)}`;
    }
    return val;
  };

  getInitials(firstName: string, lastName: string): string {
    return (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '');
  }

  getGradient(index: number): string {
    const colors = this.gradientColors[index % this.gradientColors.length];
    return `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`;
  }
}
